import {
  Business,
  Call,
  ContentCopy,
  Delete,
  Email,
  GroupWork,
  HourglassEmpty,
  InsertDriveFile,
  LiveHelp,
  LocationOn,
  Lock,
  ModeEditOutlineOutlined,
  Person,
  PushPin,
  Style,
  Tag,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
} from "@mui/material";
import { parse } from "date-fns";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  BusinessEntityType,
  GetBusinessEntityDetailsQuery,
  ShipmentReferenceNumberType,
} from "../../../graphql/generated";
import toLatLng from "../../../utils/geo/toLaLng";
import enumLabel from "../../../utils/labels/enumLabel";
import documentTypeLabel from "../../../utils/labels/documentTypeLabel";
import {
  fallbackTimezone,
  formatTime,
} from "../../../utils/labels/formatDateTime";
import formatPhoneNumber from "../../../utils/labels/formatPhoneNumber";
import formatUrl from "../../../utils/labels/formatUrl";
import personLabel from "../../../utils/labels/personLabel";
import InfoBlock from "../../common/InfoBlock";
import LabeledInfo from "../../common/LabeledInfo";
import Map, { createMarker } from "../../common/Map/Map";
import { dayLabel } from "../OpeningHoursForm/OpeningHoursForm";
import commodityLabel from "../../../utils/labels/commodityLabel";
import RoleGuard from "../../account/Access/RoleGuard";
import FeatureGuard, { Feature } from "../../account/Access/FeatureGuard";
import { formatInTimeZone } from "date-fns-tz";
import { capitalize } from "lodash";
import ChipTagsList from "../../common/ChipTagsList/ChipTagsList";
import GroupsDisplayContainer from "../GroupsDisplay";
import CustomFieldsDisplayContainer from "../../custom-field-definitions/CustomFieldsDisplay";
import trailerTypeLabel from "../../../utils/labels/trailerTypeLabel";
import SupplierContractsListContainer from "../SuppliersList/SupplierContractsList";
import StorageFacilityLevelBar from "../../forecasting/ForecastingDashboard/StorageFacilityLevelBar";
import BusinessEntityLinkContainer from "../BusinessEntityLink";
import OrgSettingsGuard from "../../account/Access/OrgSettingsGuard";
import invoiceConsolidationCriteriaLabel from "../../../utils/labels/invoiceConsolidationCriteriaLabel";
import DwellTimeUpticksListContainer from "../DwellTimeUpticksList";
import { testIds } from "./BusinessEntityDetails.testIds";

type BusinessEntityDetailsData =
  GetBusinessEntityDetailsQuery["businessEntityById"];

type BusinessEntityDetailsProps = {
  businessEntity: BusinessEntityDetailsData;
  onEdit: () => void;
  onDelete: () => void;
};

function BusinessEntityDetails({
  businessEntity,
  onEdit,
  onDelete,
}: BusinessEntityDetailsProps) {
  const { t } = useTranslation(["business", "common", "orders", "assets"]);
  const [isAddressMapOpen, setIsAddressMapOpen] = useState(false);

  const businessEntityTypes = [
    businessEntity?.type,
    ...(businessEntity?.additionalTypes || []),
  ].filter(Boolean) as BusinessEntityType[];

  const isBusinessOneOfType = (types: BusinessEntityType[]) =>
    types.some((type) => businessEntityTypes.includes(type));

  const customerPortalUrl = businessEntity.accessToken
    ? `${window.location.origin}/customer-portal?token=${businessEntity.accessToken}`
    : null;
  return (
    <Box>
      <Stack direction="row" flexDirection="row-reverse" spacing={2}>
        <Stack direction="row" flexDirection="row-reverse" spacing={2}>
          <RoleGuard roles={["Carrier Admin"]}>
            <IconButton data-testid={testIds.businessEntityDetailsDelete} onClick={onDelete} color="error">
              <Delete />
            </IconButton>
          </RoleGuard>
        </Stack>
        <RoleGuard
          roles={[
            "Carrier Admin",
            "Manager",
            "Clerical",
            isBusinessOneOfType([
              BusinessEntityType.Customer,
              BusinessEntityType.Broker,
            ])
              ? "Accounting"
              : null,
          ]}
        >
          <Stack direction="row" flexDirection="row-reverse" spacing={2}>
            <IconButton data-testid={testIds.businessEntityDetailsEdit} onClick={onEdit} color="primary">
              <ModeEditOutlineOutlined />
            </IconButton>
          </Stack>
        </RoleGuard>
      </Stack>
      <Grid container spacing={3}>
        <Grid item sm={6}>
          <InfoBlock title={t("business:details", "Business Details")}>
            <LabeledInfo
              title={t("business:name", "Name")}
              value={businessEntity.name}
              icon={<Business />}
            />
            {businessEntity.type !== BusinessEntityType.Carrier ? (
              <LabeledInfo
                title={t("business:types", "Types")}
                value={[
                  businessEntity.type,
                  ...(businessEntity.additionalTypes || []),
                ]
                  .map((entity) =>
                    t(
                      // @ts-expect-error
                      `business:businessEntityType.${entity}`,
                      enumLabel(entity),
                    ),
                  )
                  .join(", ")}
                icon={<PushPin />}
              />
            ) : (
              <LabeledInfo
                title={t("business:carrier.types", "Carrier Types")}
                value={(businessEntity.trailerTypes || [])
                  .map(trailerTypeLabel)
                  .join(", ")}
                icon={<PushPin />}
              />
            )}
            <LabeledInfo
              title={t("business:code", "Business code")}
              value={businessEntity.code}
              icon={<Tag />}
            />
            {isBusinessOneOfType([
              BusinessEntityType.Broker,
              BusinessEntityType.Carrier,
            ]) ? (
              <LabeledInfo
                title="MC Number"
                value={businessEntity.mcNumber}
                icon={<Tag />}
              />
            ) : null}
            <LabeledInfo
              title="Federal ID"
              value={businessEntity.federalId}
              icon={<Tag />}
            />
            {isBusinessOneOfType([BusinessEntityType.Carrier]) ? (
              <>
                <LabeledInfo
                  title="SCAC"
                  value={businessEntity.standardCarrierAlphaCode}
                  icon={<Tag />}
                />
                <LabeledInfo
                  title="DOT#"
                  value={businessEntity.dotNumber}
                  icon={<Tag />}
                />
                <LabeledInfo
                  title={capitalize(
                    t("business:payToProfile.one", "Pay to Profile"),
                  )}
                  value={
                    businessEntity.payToProfileId ? (
                      <Link data-testid={testIds.businessEntityDetailsPayToProfile}
                        href={`/payto/details/${businessEntity.payToProfileId}`}
                      >
                        {t("common:open", "Open")}
                      </Link>
                    ) : null
                  }
                  icon={<Tag />}
                />
              </>
            ) : null}

            {isBusinessOneOfType([BusinessEntityType.Receiver]) && (
              <>
                <LabeledInfo
                  title={t(
                    "business:receiver.parentCustomer",
                    "Parent customer",
                  )}
                  value={
                    <BusinessEntityLinkContainer
                      entityId={businessEntity.parentBusinessEntityId}
                    />
                  }
                />
                <LabeledInfo
                  title={t(
                    "business:receiver.defaultShipper",
                    "Default shipper",
                  )}
                  value={
                    <BusinessEntityLinkContainer
                      entityId={businessEntity.defaultShipperId}
                    />
                  }
                />
                <FeatureGuard feature={Feature.CommodityManagement}>
                  <LabeledInfo
                    title={t("business:commodities", "Commodities")}
                    value={
                      businessEntity.commodities?.length
                        ? businessEntity.commodities
                            .map((c) => commodityLabel(c))
                            .join(", ")
                        : null
                    }
                  />
                </FeatureGuard>
              </>
            )}

            <LabeledInfo
              title={t("assets:status", "Status")}
              value={t(
                `commo:statusTypes.${businessEntity.status}`,
                enumLabel(businessEntity.status) || "N/A",
              )}
              icon={<LiveHelp />}
            />
            <LabeledInfo
              title={t("common:tags")}
              value={<ChipTagsList value={businessEntity.tags || []} />}
              icon={<Style />}
            />
            <LabeledInfo
              title={t("common:groups")}
              value={
                <GroupsDisplayContainer groupIds={businessEntity.groupIds} />
              }
              icon={<GroupWork />}
            />
          </InfoBlock>
        </Grid>
        <Grid item sm={6}>
          <InfoBlock title={t("business:contactDetails", "Contact Details")}>
            <LabeledInfo
              title={t("business:title", "Title")}
              value={businessEntity.contact.title}
              icon={<Person />}
            />
            <LabeledInfo
              title={t("business:name", "Name")}
              value={personLabel(businessEntity.contact)}
              icon={<Person />}
            />
            <LabeledInfo
              title={t("common:email", "Email Address")}
              value={businessEntity.contact.email}
              icon={<Email />}
            />
            <LabeledInfo
              title={t("common:phoneNumber", "Phone Number")}
              value={formatPhoneNumber(businessEntity.contact.phoneNumber)}
              icon={<Call />}
            />
            <LabeledInfo
              title={t("common:faxNumber", "Fax Number")}
              value={businessEntity.contact.faxNumber}
              icon={<Call />}
            />
            <LabeledInfo
              title={t("common:url", "Website")}
              value={
                businessEntity.contact.url ? (
                  <Link data-testid={testIds.businessEntityDetailsWebsite}
                    href={`${formatUrl(businessEntity.contact.url)}`}
                    component="a"
                    target="_blank"
                  >
                    {formatUrl(businessEntity.contact.url)}
                  </Link>
                ) : null
              }
            />
            <LabeledInfo
              title={t("common:extensionNumber", "Extension Number")}
              value={businessEntity.contact.extensionNumber}
              icon={<Call />}
            />
            <FeatureGuard feature={Feature.CustomerPortal}>
              {customerPortalUrl ? (
                <LabeledInfo
                  title={t("customer.portalAccessUrl", "Portal Access URL")}
                  value={
                    <>
                      <Link data-testid={testIds.businessEntityDetailsCustomerPortalUrl} href={customerPortalUrl} component="a">
                        {customerPortalUrl}
                      </Link>
                      {/* copy button */}
                      <IconButton data-testid={testIds.businessEntityDetailsCopyCustomerPortalUrl}
                        onClick={() => {
                          navigator.clipboard.writeText(customerPortalUrl);
                        }}
                      >
                        <ContentCopy />
                      </IconButton>
                    </>
                  }
                  icon={<Lock />}
                />
              ) : // copy button

              null}
            </FeatureGuard>

            {isBusinessOneOfType([BusinessEntityType.Receiver]) && (
              <LabeledInfo
                title={t(
                  "business:shipmentDocumentsToReceive",
                  "Shipment Documents To Receive",
                )}
                value={
                  <ChipTagsList
                    value={businessEntity.shipmentDocumentsToReceive || []}
                  />
                }
                icon={<InsertDriveFile />}
              />
            )}
          </InfoBlock>
        </Grid>
        <Grid item sm={4}>
          <InfoBlock title={t("business:physicalAddress", "Physical Address")}>
            <LabeledInfo data-testid="business-entity-details-open-address-map"
              title={t("business:physicalAddress", "Physical Address")}
              value={businessEntity.address.label}
              icon={<LocationOn />}
              onClick={() => setIsAddressMapOpen(true)}
            />
            {isBusinessOneOfType([
              BusinessEntityType.Shipper,
              BusinessEntityType.Receiver,
            ]) && (
              <LabeledInfo
                title={t("business:plannedDwellTime", "Planned Dwell Time")}
                value={
                  businessEntity.plannedDwellTimeInMinutes !== null &&
                  businessEntity.plannedDwellTimeInMinutes !== undefined
                    ? `${businessEntity.plannedDwellTimeInMinutes} minutes`
                    : "N/A"
                }
                icon={<HourglassEmpty />}
              />
            )}
          </InfoBlock>
        </Grid>
        <Grid item sm={4}>
          {isBusinessOneOfType([
            BusinessEntityType.Customer,
            BusinessEntityType.Broker,
          ]) ? (
            <>
              <InfoBlock
                title={t("business:billingAddress", "Billing Address")}
              >
                <LabeledInfo
                  title={t("common:address.line1", "Address Line 1")}
                  value={businessEntity.billingAddress?.line1}
                />
                <LabeledInfo
                  title={t("common:address.line2", "Address Line 2")}
                  value={businessEntity.billingAddress?.line2}
                />
                <LabeledInfo
                  title={t("common:address.postalCode", "Postal Code")}
                  value={businessEntity.billingAddress?.postalCode}
                />
                <LabeledInfo
                  title={t("common:address.city", "City")}
                  value={businessEntity.billingAddress?.city}
                />
                <LabeledInfo
                  title={t("common:address.state", "State")}
                  value={businessEntity.billingAddress?.state}
                />
                <LabeledInfo
                  title={t("common:address.country", "Country")}
                  value={businessEntity.billingAddress?.country}
                />
                <LabeledInfo
                  title={t("common:email", "Adresse Email")}
                  value={businessEntity.billingEmail}
                />
              </InfoBlock>
              <InfoBlock title={t("business:invoicing", "Invoicing")}>
                <LabeledInfo
                  title={t("business:billingTerms", "Billing Terms")}
                  value={
                    businessEntity.billingTermsDay
                      ? t("business:countDays", {
                          count: businessEntity.billingTermsDay,
                        })
                      : null
                  }
                />
                <OrgSettingsGuard
                  evaluate={(settings) =>
                    !!settings.invoicing?.enableConsolidatedInvoicing
                  }
                >
                  <LabeledInfo
                    title={t(
                      "business:invoiceConsolidationCriteria",
                      "Invoice Consolidation Criteria",
                    )}
                    value={
                      businessEntity.billingInvoiceConsolidationCriteria
                        ? invoiceConsolidationCriteriaLabel(
                            businessEntity.billingInvoiceConsolidationCriteria,
                          )
                        : "N/A"
                    }
                  />
                  <LabeledInfo
                    title={t("business:weekStartsOn", "Week Start Day")}
                    value={
                      businessEntity.billingWeekStartsOn
                        ? dayLabel(businessEntity.billingWeekStartsOn)
                        : "N/A"
                    }
                  />
                </OrgSettingsGuard>
                <LabeledInfo
                  title={t("business:referenceNumberTypes", "Reference Number")}
                  value={
                    <Stack spacing={1}>
                      {Object.values(ShipmentReferenceNumberType).map(
                        (refFieldType) => {
                          const defaultRefFields =
                            businessEntity.defaultReferenceNumbers?.filter(
                              (refField) =>
                                refField.referenceNumberType === refFieldType,
                            );

                          return (
                            <Stack>
                              <Typography>
                                {t(
                                  `orders:referenceNumberType.${
                                    refFieldType as ShipmentReferenceNumberType
                                  }`,
                                )}
                              </Typography>
                              <Stack direction="row" spacing={1}>
                                {defaultRefFields &&
                                defaultRefFields.length !== 0
                                  ? defaultRefFields.map((refField) => (
                                      <Chip
                                        label={
                                          refField.referenceNumberValue ||
                                          "Default empty"
                                        }
                                      />
                                    ))
                                  : "N/A"}
                              </Stack>
                            </Stack>
                          );
                        },
                      )}
                    </Stack>
                  }
                />
              </InfoBlock>
            </>
          ) : null}
        </Grid>
        <Grid item sm={4}>
          {isBusinessOneOfType([
            BusinessEntityType.Customer,
            BusinessEntityType.Broker,
          ]) ? (
            <InfoBlock title={t("business:remitAddress", "Remit Address")}>
              <LabeledInfo
                title={t("business:remitCompanyName", "Remit Company Name")}
                value={businessEntity.remitCompanyName}
              />
              <LabeledInfo
                title={t("common:address.line1", "Address Line 1")}
                value={businessEntity.remitAddress?.line1}
              />
              <LabeledInfo
                title={t("common:address.line2", "Address Line 2")}
                value={businessEntity.remitAddress?.line2}
              />
              <LabeledInfo
                title={t("common:address.postalCode", "Postal Code")}
                value={businessEntity.remitAddress?.postalCode}
              />
              <LabeledInfo
                title={t("common:address.city", "City")}
                value={businessEntity.remitAddress?.city}
              />
              <LabeledInfo
                title={t("common:address.state", "State")}
                value={businessEntity.remitAddress?.state}
              />
              <LabeledInfo
                title={t("common:address.country", "Country")}
                value={businessEntity.remitAddress?.country}
              />
              <LabeledInfo
                title={t("remitEmail", "Email")}
                value={businessEntity.remitEmail}
              />
            </InfoBlock>
          ) : null}
        </Grid>

        <Grid item xs={12}>
          <CustomFieldsDisplayContainer
            customFields={businessEntity.customFields}
          />
        </Grid>
        {isBusinessOneOfType([
          BusinessEntityType.Shipper,
          BusinessEntityType.Receiver,
        ]) ? (
          <Grid item sm={12}>
            <InfoBlock title={t("business:operatingHours", "Operating Hours")}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{capitalize(t("days_other", "Days"))}</TableCell>
                    <TableCell>{t("openingTime", "Opening time")}</TableCell>
                    <TableCell>{t("closingTime", "Closing time")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businessEntity.openingSchedules?.map((schedule) => (
                    <TableRow key={schedule.days.join("-")}>
                      <TableCell>
                        {schedule.days.map(dayLabel).join(", ")}
                      </TableCell>
                      <TableCell>
                        {/* Opening and closing hours are relative times, not absolute, so we can display them as is */}
                        {/* then display the timezone separately */}
                        {formatTime(
                          parse(schedule.openingTime, "HH:mm", new Date()),
                          null,
                          false,
                        )}
                        {formatInTimeZone(
                          new Date(),
                          businessEntity.addressTimezone || fallbackTimezone,
                          " (zzz)",
                        )}
                      </TableCell>
                      <TableCell>
                        {formatTime(
                          parse(schedule.closingTime, "HH:mm", new Date()),
                          null,
                          false,
                        )}
                        {formatInTimeZone(
                          new Date(),
                          businessEntity.addressTimezone || fallbackTimezone,
                          " (zzz)",
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </InfoBlock>
          </Grid>
        ) : null}

        <Grid item sm={12}>
          <InfoBlock
            title={t("business:additionalContacts", "Additional Contacts")}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("common:title", "Title")}</TableCell>
                  <TableCell>{t("common:firstName", "First Name")}</TableCell>
                  <TableCell>{t("common:lastName", "Last Name")}</TableCell>
                  <TableCell>{t("common:email", "Adresse Email")}</TableCell>
                  <TableCell>
                    {t("common:extensionNumber", "Extension Number")}
                  </TableCell>
                  <TableCell>{t("common:faxNumber", "Fax Number")}</TableCell>
                  <TableCell>{t("common:url", "Website")}</TableCell>
                  <TableCell>
                    {t("common:phoneNumber", "Phone Number")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {businessEntity.additionalContacts?.map((contact, index) => (
                  <TableRow key={contact.email || contact.phoneNumber || index}>
                    <TableCell>{contact.title}</TableCell>
                    <TableCell>{contact.firstname}</TableCell>
                    <TableCell>{contact.lastname}</TableCell>
                    <TableCell>{contact.email}</TableCell>
                    <TableCell>{contact.extensionNumber}</TableCell>
                    <TableCell>{contact.faxNumber}</TableCell>
                    <TableCell>
                      <Link data-testid={`${
                          testIds.businessEntityDetailsContactWebsite
                        }-${
                          contact.email ||
                          contact.phoneNumber ||
                          `${contact.firstname}-${contact.lastname}`
                        }`}
                        href={`${formatUrl(contact.url)}`}
                        component="a"
                        target="_blank"
                      >
                        {formatUrl(contact.url)}
                      </Link>
                    </TableCell>
                    <TableCell>
                      {formatPhoneNumber(contact.phoneNumber)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </InfoBlock>
        </Grid>

        <Grid item sm={12}>
          <InfoBlock title={t("common:documents.many")}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("common:name")}</TableCell>
                  <TableCell
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    {t("common:documents.one")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {businessEntity.documents?.map((document) => (
                  <TableRow key={document.url}>
                    <TableCell>{document.name}</TableCell>
                    <TableCell>
                      <Link data-testid={`${testIds.businessEntityDetailsOpenDocument}-${document.url}`} href={document.url} component="a" target="_blank">
                        Open
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </InfoBlock>
        </Grid>

        <Grid item sm={12}>
          <InfoBlock title={t("common:notes", "Notes")}>
            {businessEntity.notes && businessEntity.notes.length > 0 ? (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t("orders:notes.note", "Note")}</TableCell>
                    <TableCell>
                      {t("orders:notes.visibility", "Visibility")}
                    </TableCell>
                    <TableCell>
                      {t("orders:notes.addToOrder", "Add to order")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businessEntity.notes.map((note, index) => (
                    <TableRow key={index}>
                      <TableCell style={{ whiteSpace: "pre-wrap" }}>
                        {note.content}
                      </TableCell>
                      <TableCell>
                        {(note.accessLevel || []).map((level) => (
                          <Chip
                            key={level}
                            label={capitalize(level)}
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={note.addToShipment}
                          disabled
                          size="small"
                          sx={{ p: 0 }}
                        inputProps={{ "data-testid": `${testIds.businessEntityDetailsAddNoteToShipment}-${note.content}` } as React.InputHTMLAttributes<HTMLInputElement>}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2">
                {t("orders:notes.noNotes", "No notes available")}
              </Typography>
            )}
          </InfoBlock>
        </Grid>

        {isBusinessOneOfType([BusinessEntityType.Customer]) && (
          <Grid item sm={12}>
            <InfoBlock
              title={t(
                "business:documentRequirements",
                "Document Requirements",
              )}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      {t("business:documentType", "Document Type")}
                    </TableCell>
                    <TableCell>
                      {t("business:documentName", "Document Name")}
                    </TableCell>
                    <TableCell>
                      {t(
                        "business:requiredForInvoicing",
                        "Required for Invoicing",
                      )}
                    </TableCell>
                    <TableCell>
                      {t("business:attachToInvoice", "Attach to Invoice")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {businessEntity.documentRequirements?.length ? (
                    businessEntity.documentRequirements.map(
                      (requirement, index) => (
                        <TableRow
                          key={`${requirement.documentType}-${requirement.documentName}-${index}`}
                        >
                          <TableCell>
                            {documentTypeLabel(requirement.documentType)}
                          </TableCell>
                          <TableCell>
                            {requirement.documentName ||
                              documentTypeLabel(requirement.documentType)}
                          </TableCell>
                          <TableCell>
                            {requirement.requiredForInvoicing ? (
                              <Chip color="primary" label={t("common:yes")} />
                            ) : (
                              <Chip label={t("common:no")} />
                            )}
                          </TableCell>
                          <TableCell>
                            {requirement.attachToInvoice ? (
                              <Chip color="primary" label={t("common:yes")} />
                            ) : (
                              <Chip label={t("common:no")} />
                            )}
                          </TableCell>
                        </TableRow>
                      ),
                    )
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {t("common:noRecords", "No records found")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </InfoBlock>
          </Grid>
        )}

        {isBusinessOneOfType([
          BusinessEntityType.Shipper,
          BusinessEntityType.Receiver,
        ]) ? (
          <Grid item sm={12}>
            <InfoBlock
              title={t("business:dwellTimeUpticks", "Dwell Time Upticks")}
            >
              <DwellTimeUpticksListContainer
                businessEntityId={businessEntity._id}
                businessEntityTimezone={businessEntity.addressTimezone}
              />
            </InfoBlock>
          </Grid>
        ) : null}

        <FeatureGuard feature={Feature.CommodityManagement}>
          {isBusinessOneOfType([BusinessEntityType.Receiver]) ? (
            <Grid item sm={12}>
              <InfoBlock title="Tanks">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tank ID</TableCell>
                      <TableCell>Capacity</TableCell>
                      <TableCell>Unit</TableCell>
                      <TableCell>Commodity</TableCell>
                      <TableCell>Default Supplier</TableCell>
                      <TableCell>Level</TableCell>
                      <TableCell>Planned Dwell Time</TableCell>
                      <TableCell>Custom Fields</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {businessEntity.storageFacilities?.map(
                      (storageFacility) => (
                        <TableRow>
                          <TableCell>{storageFacility.identifier}</TableCell>
                          <TableCell
                            sx={{
                              textAlign: "center",
                            }}
                          >
                            {storageFacility.capacity}
                            <br />
                            {storageFacility.safeFillLevel ? (
                              <Typography variant="caption" color="green">
                                Safe Fill: {storageFacility.safeFillLevel}
                              </Typography>
                            ) : null}
                            {storageFacility.safeFillLevel &&
                            storageFacility.shutDownLevel ? (
                              <br />
                            ) : (
                              ""
                            )}
                            {storageFacility.shutDownLevel ? (
                              <Typography variant="caption" color="red">
                                Shut Down: {storageFacility.shutDownLevel}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {enumLabel(storageFacility.unit)}
                          </TableCell>
                          <TableCell>
                            {storageFacility.commodities
                              ?.map((commodity) => commodityLabel(commodity))
                              .join(", ")}
                          </TableCell>
                          <TableCell>
                            {storageFacility.defaultSupplier
                              ? `
                            (${storageFacility.defaultSupplier?.code})
                            ${storageFacility.defaultSupplier?.name}`
                              : null}
                          </TableCell>
                          <TableCell>
                            <StorageFacilityLevelBar
                              storageFacility={storageFacility}
                            />
                          </TableCell>
                          <TableCell>
                            {storageFacility.plannedDwellTimeInMinutes !== null &&
                            storageFacility.plannedDwellTimeInMinutes !== undefined
                              ? `${storageFacility.plannedDwellTimeInMinutes} minutes`
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            <CustomFieldsDisplayContainer
                              customFields={storageFacility.customFields}
                              simple
                            />
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </InfoBlock>
            </Grid>
          ) : null}
        </FeatureGuard>

        {businessEntity.type === BusinessEntityType.Supplier ? (
          <Grid item xs={12}>
            <InfoBlock
              title={capitalize(
                t("business:supplier.contract.many", "Contrats"),
              )}
            >
              <SupplierContractsListContainer />
            </InfoBlock>
          </Grid>
        ) : null}
      </Grid>

      <Dialog
        open={isAddressMapOpen}
        onClose={() => setIsAddressMapOpen(false)}
      >
        <DialogTitle>{businessEntity.address.label}</DialogTitle>

        <DialogContent>
          <Map
            center={toLatLng(businessEntity.address.coordinates)}
            zoom={13}
            markers={[
              createMarker(toLatLng(businessEntity.address.coordinates)),
            ]}
          />
        </DialogContent>

        <DialogActions>
          <Button data-testid={testIds.businessEntityDetailsClose} onClick={() => setIsAddressMapOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BusinessEntityDetails;
